import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ONBOARDING_STEPS } from '@/routes/util';
import {
  useUserPreferencesStore,
  selectSetCurrentOrganizationId,
  selectSetCurrentWorkspaceId,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import {
  useCurrentUser,
  useUpdateCurrentUser,
  type UpdateCurrentUserMutationBody,
} from '@/generated/hooks/agora/v1/user';

/**
 * Syncs URL organization and workspace context to the Zustand store.
 * This ensures store is always in sync with the URL
 * Should be called once in MainLayout
 */
export const useSyncUrlParamsToStore = () => {
  const { orgId, workspaceId, onboardingStep } = useParams<{
    orgId?: string;
    workspaceId?: string;
    onboardingStep?: string;
  }>();
  const [searchParams] = useSearchParams();
  const onboardingOrgId =
    onboardingStep === ONBOARDING_STEPS.WORKSPACE
      ? searchParams.get('orgId')
      : null;
  const selectedOrgId = orgId || onboardingOrgId;
  const setCurrentOrganizationId = useUserPreferencesStore(
    selectSetCurrentOrganizationId,
  );
  const setCurrentWorkspaceId = useUserPreferencesStore(
    selectSetCurrentWorkspaceId,
  );
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { mutate: updateCurrentUser } = useUpdateCurrentUser();

  // We only need the updated cache data to be available
  // We don't need to make new API call, so we disable it
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    isFetching: isCurrentUserFetching,
  } = useCurrentUser({ query: { enabled: false } });

  // eslint-disable-next-line no-restricted-syntax -- reason: Sync URL params to Zustand store when URL changes
  useEffect(() => {
    if (selectedOrgId && currentOrganizationId !== selectedOrgId) {
      setCurrentOrganizationId(selectedOrgId);
      if (onboardingOrgId) {
        setCurrentWorkspaceId('');
      }
    }
  }, [
    selectedOrgId,
    onboardingOrgId,
    currentOrganizationId,
    setCurrentOrganizationId,
    setCurrentWorkspaceId,
  ]);

  // Only update when workspaceId, and currentWorkspaceId are different
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync URL params to Zustand store when URL changes
  useEffect(() => {
    if (workspaceId && currentWorkspaceId !== workspaceId) {
      setCurrentWorkspaceId(workspaceId);
    }
  }, [workspaceId, currentWorkspaceId, setCurrentWorkspaceId]);

  const currentUserLastOrg = currentUser?.data.user?.last_org;
  const currentUserLastWorkspace = currentUser?.data.user?.last_workspace;

  // Only update last_org and last_workspace if user data is loaded and there's a difference
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync URL params to user preferences when both URL and user data are available
  useEffect(() => {
    // Don't update if user data hasn't loaded yet
    if (!currentUser || isCurrentUserLoading || isCurrentUserFetching) {
      return;
    }

    // Only update if there's an actual difference between URL params and user's stored preferences
    // We check for explicit differences to avoid unnecessary updates on initial load
    const needsOrgUpdate = orgId && currentUserLastOrg !== orgId;
    const needsWorkspaceUpdate =
      workspaceId && currentUserLastWorkspace !== workspaceId;

    if (needsOrgUpdate || needsWorkspaceUpdate) {
      const data: { last_org?: string; last_workspace?: string } = {};

      // update only if both orgId and workspaceId are available
      if (orgId && workspaceId) {
        data.last_org = orgId;
        data.last_workspace = workspaceId;

        updateCurrentUser({ data: data as UpdateCurrentUserMutationBody });
      }
    }
  }, [
    orgId,
    currentUserLastOrg,
    workspaceId,
    currentUserLastWorkspace,
    currentUser,
    isCurrentUserLoading,
    isCurrentUserFetching,
    updateCurrentUser,
  ]);
};
